import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useConversation } from '@elevenlabs/react';
import api from '../api';
import { UAParser } from 'ua-parser-js';
import {
  ElevenLabsConversationTokenRequest,
  ElevenLabsConversationEventRequest,
  ElevenLabsConversationEventResponse,
  ElevenLabsEventType,
  ElevenLabsEventContent,
} from '@dreamai/types';
import { useOpenAIRealtime, OpenAIRealtimeConfig } from './useOpenAIRealtime';

const getDeviceType = (): 'mobile' | 'desktop' | 'tablet' => {
  const parser = new UAParser();
  const device = parser.getDevice();
  if (device.type === 'mobile') return 'mobile';
  if (device.type === 'tablet') return 'tablet';
  return 'desktop';
};

export type VoiceSessionStatus = 'disconnected' | 'connecting' | 'connected';

export interface ShowTodoParams {
  description: string;
  desired_action_key: string;
  desired_action_label: string;
  spend_description?: string;
  estimated_spend?: string;
  spend_currency?: string;
}

export interface RemoveTodoParams {
  todo_id?: string;
  description?: string;
}

export interface AnswerArtifactParams {
  artifact_id: string;
  /** For multiple_choice: comma-separated choice IDs */
  selected_choice_ids?: string;
  /** For yes_or_no: 'yes' or 'no' */
  yes_or_no?: 'yes' | 'no';
  /** For form_input: JSON-encoded field values { fieldId: value } */
  form_values?: string;
  /** Desired action key to select */
  desired_action_key?: string;
  /** Free text for 'other' action */
  desired_action_custom_text?: string;
  /** Answers to clarifying questions: JSON-encoded { fieldKey: answer } */
  clarifying_answers?: string;
}

// ── Editor tool params ─────────────────────────────────────────────────────
// These back the `open_editor` / `patch_editor` / `close_editor` ElevenLabs
// client tools, which collaboratively edit a markdown document on-screen
// during a voice call. The agent-side schema is declared in the ElevenLabs
// agent config (same way as `add_todo`); the params below are what the client
// callbacks receive from the tool invocation.

export interface ShowProjectParams {
  project_name: string;
  direction: string;
}

export interface OpenEditorParams {
  editor_type: 'document' | 'email_draft';
  title: string;
  initial_content?: string;
  /** email_draft only: */
  oauth_id?: number;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
}

/**
 * Patch op shape mirrors `PatchOp` from `lib/editorPatch.ts` but typed as a
 * plain unknown here to keep the hook decoupled from the engine. The hook
 * forwards raw ops to the parent, which runs `applyPatches`.
 *
 * Optional field overrides let the agent update header metadata (title,
 * recipients, subject) in the same tool call as content patches — previously
 * only `content` was patchable, which meant the agent couldn't edit cc/bcc/
 * subject once the editor was open. Any omitted field is left untouched.
 */
export interface PatchEditorParams {
  editor_id: string;
  /** Content patch ops — may be empty if the call only updates fields. */
  edits: unknown[];
  /** Optional replacement for the card's title (doc title / email subject fallback). */
  title?: string;
  /** email_draft only — replace the To field. Empty string clears it. */
  to?: string;
  /** email_draft only — replace the Cc field. */
  cc?: string;
  /** email_draft only — replace the Bcc field. */
  bcc?: string;
  /** email_draft only — replace the Subject field. */
  subject?: string;
}

export interface CloseEditorParams {
  editor_id: string;
  reason?: string;
}

/**
 * Outcome of a patch_editor handler invocation.
 *
 * `severity: 'error'` is for cases where NOTHING in the patch took effect —
 * 0/N edits applied AND no header fields updated. The voice tool wrapper
 * promotes these to a `[ERROR] patch_editor` contextual update so the agent
 * sees them as a hard failure on its next turn (and re-drafts) instead of
 * the muted `[TOOL RESULT]` it gets for partial success. The single biggest
 * cause of "the editor on screen is blank but the agent insists it drafted
 * the email" was the agent missing this signal — see managers-webrtc.ts.
 */
export interface PatchEditorResult {
  summary: string;
  severity: 'ok' | 'error';
}

export interface UseVoiceSessionOptions {
  agentId?: string;
  threadId?: string;
  voiceId?: string;
  structuredCallId?: string;
  agentFirstMessage?: string;
  isFirstCall?: boolean;
  dailyStandupId?: string;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: string) => void;
  onShowTodo?: (params: ShowTodoParams) => string;
  onRemoveTodo?: (params: RemoveTodoParams) => string;
  onAnswerArtifact?: (params: AnswerArtifactParams) => Promise<string>;
  /** Shows a project confirmation card; returns the card id or 'DUPLICATE'. */
  onShowProject?: (params: ShowProjectParams) => string;
  /** Opens a new markdown editor card; returns the card id or 'DUPLICATE'. */
  onOpenEditor?: (params: OpenEditorParams) => string;
  /** Applies agent-authored patches; returns a human-readable summary. */
  onPatchEditor?: (params: PatchEditorParams) => PatchEditorResult;
  /** Dismisses an unsaved editor card. */
  onCloseEditor?: (params: CloseEditorParams) => string;
}

export interface UseVoiceSessionReturn {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  status: VoiceSessionStatus;
  isSpeaking: boolean;
  isThinking: boolean;
  /** True when isThinking is true and the agent hasn't spoken yet (first turn) */
  isFirstThinking: boolean;
  audioLevel: number;
  inputLevel: number;
  outputLevel: number;
  /** Real-time output level ref — updates every animation frame, use for canvas animations */
  outputLevelRef: React.MutableRefObject<number>;
  error: string | null;
  sendContextualUpdate: (text: string) => void;
}

interface WebRTCTokenResponse {
  provider: 'elevenlabs' | 'openai';
  thread_id: string | null;
  agent_id: string;
  tracking_id?: string;
  token?: string;
  agentConfig?: any;
  openaiConfig?: OpenAIRealtimeConfig;
  openaiClientSecret?: string;
  memoriesContext?: string;
}

export function useVoiceSession(
  options: UseVoiceSessionOptions = {},
): UseVoiceSessionReturn {
  const {
    agentId,
    threadId,
    voiceId,
    structuredCallId,
    agentFirstMessage,
    isFirstCall,
    dailyStandupId,
    onConnectionChange,
    onError: onErrorCallback,
    onShowTodo,
    onRemoveTodo,
    onAnswerArtifact,
    onShowProject,
    onOpenEditor,
    onPatchEditor,
    onCloseEditor,
  } = options;

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [error, setError] = useState<string | null>(null);
  const [voiceProvider, setVoiceProvider] = useState<
    'elevenlabs' | 'openai' | null
  >(null);
  const voiceProviderRef = useRef<'elevenlabs' | 'openai' | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputLevel, setOutputLevel] = useState(0);
  const outputLevelRef = useRef(0);

  // VAD-based thinking detection refs
  const wasUserSpeakingRef = useRef(false);
  const hasAgentSpokenRef = useRef(false);
  const silenceStartRef = useRef<number>(0);
  const SPEECH_THRESHOLD = 0.08; // input level above this = user is speaking
  const SILENCE_DEBOUNCE_MS = 400; // silence duration before switching to "thinking"

  const threadIdRef = useRef<string | null>(null);
  const trackingIdRef = useRef<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastContextFetchRef = useRef<number>(0);
  const contextFetchDebounceMs = 500;
  const sendContextualUpdateRef = useRef<((text: string) => void) | null>(null);
  const cancelledRef = useRef(false);

  // Stable refs for callbacks
  const onConnectionChangeRef = useRef(onConnectionChange);
  const onErrorCallbackRef = useRef(onErrorCallback);
  const onShowTodoRef = useRef(onShowTodo);
  const onRemoveTodoRef = useRef(onRemoveTodo);
  const onAnswerArtifactRef = useRef(onAnswerArtifact);
  const onShowProjectRef = useRef(onShowProject);
  const onOpenEditorRef = useRef(onOpenEditor);
  const onPatchEditorRef = useRef(onPatchEditor);
  const onCloseEditorRef = useRef(onCloseEditor);
  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange;
    onErrorCallbackRef.current = onErrorCallback;
    onShowTodoRef.current = onShowTodo;
    onRemoveTodoRef.current = onRemoveTodo;
    onAnswerArtifactRef.current = onAnswerArtifact;
    onShowProjectRef.current = onShowProject;
    onOpenEditorRef.current = onOpenEditor;
    onPatchEditorRef.current = onPatchEditor;
    onCloseEditorRef.current = onCloseEditor;
  }, [
    onConnectionChange,
    onErrorCallback,
    onShowTodo,
    onRemoveTodo,
    onAnswerArtifact,
    onShowProject,
    onOpenEditor,
    onPatchEditor,
    onCloseEditor,
  ]);

  const handleError = useCallback((errorMessage: string) => {
    console.error('VoiceSession Error:', errorMessage);
    setError(errorMessage);
    onErrorCallbackRef.current?.(errorMessage);
  }, []);

  // --- Send event to backend ---
  const sendEventToBackend = useCallback(
    async (eventType: ElevenLabsEventType, content: ElevenLabsEventContent) => {
      try {
        const headers: Record<string, string> = {};
        if (isAuthenticated) {
          const accessToken = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const requestBody: ElevenLabsConversationEventRequest = {
          thread_id: threadIdRef.current || '',
          agent_id: agentId || undefined,
          event_type: eventType,
          content,
          device_type: getDeviceType(),
          ...(trackingIdRef.current
            ? { tracking_id: trackingIdRef.current }
            : {}),
        };

        await api.post<ElevenLabsConversationEventResponse>(
          '/webrtc/events',
          requestBody,
          { headers },
        );
      } catch (err) {
        console.error(`Failed to send ${eventType} event:`, err);
      }
    },
    [agentId, isAuthenticated, getAccessTokenSilently],
  );

  // --- Message handler ---
  const handleVoiceMessage = useCallback(
    async (source: 'user' | 'ai', message: string) => {
      console.log(`[VoiceSession] message:`, { source, message });

      if (source === 'ai') {
        sendEventToBackend(ElevenLabsEventType.AI_MESSAGE, {
          message,
          timestamp: Date.now(),
        });
      } else if (source === 'user') {
        sendEventToBackend(ElevenLabsEventType.USER_MESSAGE, {
          message,
          timestamp: Date.now(),
        });

        // Context updates disabled — Custom LLM endpoint handles per-turn memory injection
        // directly before forwarding to Claude, eliminating the race condition.
        // const now = Date.now();
        // if (now - lastContextFetchRef.current >= contextFetchDebounceMs) {
        //   lastContextFetchRef.current = now;
        //   try {
        //     const headers: Record<string, string> = {};
        //     if (isAuthenticated) {
        //       const accessToken = await getAccessTokenSilently();
        //       headers.Authorization = `Bearer ${accessToken}`;
        //     }
        //     const response = await api.post<{
        //       context: string | null;
        //       reason?: string;
        //       stats?: { factCount: number; relationshipCount: number; guidanceCount: number; calendarEventCount: number };
        //     }>('/webrtc/token/context', { message, thread_id: threadIdRef.current }, { headers });
        //     if (response.data.context) {
        //       sendContextualUpdateRef.current?.(response.data.context);
        //     }
        //   } catch (err) {
        //     console.error('[Context] Failed to fetch context:', err);
        //   }
        // }
      }
    },
    [sendEventToBackend, isAuthenticated, getAccessTokenSilently],
  );

  // --- Audio level monitoring ---
  // Throttle React state updates to ~15fps to avoid excessive re-renders
  const lastStateUpdateRef = useRef(0);
  const STATE_UPDATE_INTERVAL_MS = 66; // ~15fps

  const startAudioLevelMonitoring = useCallback(
    (provider: 'elevenlabs' | 'openai', conv: any) => {
      // Stop any existing loop before starting a new one
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      const monitor = () => {
        try {
          let inLevel = 0;
          let outLevel = 0;

          if (typeof conv.getInputByteFrequencyData === 'function') {
            const data = conv.getInputByteFrequencyData();
            if (data && data.length > 0) {
              const midRange = data.slice(10, 50);
              const avg =
                midRange.reduce((s: number, v: number) => s + v, 0) /
                midRange.length;
              inLevel = Math.min(avg / 80, 1.5);
            }
          }

          if (typeof conv.getOutputByteFrequencyData === 'function') {
            const data = conv.getOutputByteFrequencyData();
            if (data && data.length > 0) {
              const midRange = data.slice(10, 50);
              const avg =
                midRange.reduce((s: number, v: number) => s + v, 0) /
                midRange.length;
              // Peak fluctuates much more than avg for TTS — blend for dynamics
              let peak = 0;
              for (let i = 0; i < midRange.length; i++) {
                if (midRange[i] > peak) peak = midRange[i];
              }
              outLevel = Math.min((avg * 0.4 + peak * 0.6) / 100, 1.5);
            }
          }

          // Always update the ref at full frame rate (for canvas animations)
          outputLevelRef.current = outLevel;

          // VAD-based thinking detection:
          // When user's mic goes from active → silent, start "thinking" after debounce.
          // This fires much earlier than onMessage(user) which waits for STT.
          const now = performance.now();
          if (inLevel > SPEECH_THRESHOLD) {
            // User is speaking
            wasUserSpeakingRef.current = true;
            silenceStartRef.current = 0;
            setIsThinking(false);
          } else if (wasUserSpeakingRef.current && outLevel < 0.01) {
            // User was speaking, now silent, and agent isn't speaking
            if (silenceStartRef.current === 0) {
              silenceStartRef.current = now;
            } else if (now - silenceStartRef.current > SILENCE_DEBOUNCE_MS) {
              setIsThinking(true);
              wasUserSpeakingRef.current = false;
            }
          }

          // Only push to React state at throttled rate
          if (now - lastStateUpdateRef.current >= STATE_UPDATE_INTERVAL_MS) {
            lastStateUpdateRef.current = now;
            setInputLevel(inLevel);
            setOutputLevel(outLevel);
            setAudioLevel(Math.max(inLevel, outLevel));
          }
        } catch {
          // Audio monitoring not available
        }

        animationFrameRef.current = requestAnimationFrame(monitor);
      };

      animationFrameRef.current = requestAnimationFrame(monitor);
    },
    [],
  );

  const stopAudioLevelMonitoring = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setAudioLevel(0);
    setInputLevel(0);
    setOutputLevel(0);
  }, []);

  // --- ElevenLabs conversation ---
  const elevenLabsConversation = useConversation({
    onConnect: () => {
      console.log('[VoiceSession] ElevenLabs connected');
      setIsConnecting(false);
      onConnectionChangeRef.current?.(true);
    },
    onModeChange: ({ mode }) => {
      console.log(`[VoiceSession] ElevenLabs mode: ${mode}`);
      // Any mode change means the agent is no longer "thinking"
      setIsThinking(false);
      if (mode === 'speaking') {
        hasAgentSpokenRef.current = true;
      }
    },
    onDisconnect: () => {
      console.log('[VoiceSession] ElevenLabs disconnected');
      stopAudioLevelMonitoring();
      onConnectionChangeRef.current?.(false);
    },
    onError: (err) => {
      console.error('[VoiceSession] ElevenLabs error:', err);
      handleError(`Conversation error: ${err}`);
    },
    onMessage: async (message) => {
      if (message.source === 'ai' && message.message) {
        setIsThinking(false);
        handleVoiceMessage('ai', message.message);
      } else if (message.source === 'user' && message.message) {
        handleVoiceMessage('user', message.message);
      }
    },
    clientTools: {
      add_todo: async (params: Record<string, unknown>) => {
        console.log('[VoiceSession] add_todo client tool called:', params);
        const cardId = onShowTodoRef.current?.({
          description: (params.description as string) || '',
          desired_action_key:
            (params.desired_action_key as string) || 'research',
          desired_action_label: (params.desired_action_label as string) || '',
          ...(params.spend_description
            ? { spend_description: params.spend_description as string }
            : {}),
          ...(params.estimated_spend
            ? { estimated_spend: params.estimated_spend as string }
            : {}),
          ...(params.spend_currency
            ? { spend_currency: params.spend_currency as string }
            : {}),
        });
        if (cardId === 'DUPLICATE') {
          return 'This todo already exists on screen — no need to add it again. Do NOT call add_todo again for this item.';
        }
        return `Todo "${(params.description as string) || ''}" has been ADDED to the user's screen (id: ${cardId}). It is now visible. Do NOT add this todo again — it is already on screen. The user can confirm or dismiss it. You can remove it with remove_todo if needed.`;
      },
      remove_todo: async (params: Record<string, unknown>) => {
        console.log('[VoiceSession] remove_todo client tool called:', params);
        const result = onRemoveTodoRef.current?.({
          todo_id: params.todo_id as string | undefined,
          description: params.description as string | undefined,
        });
        return result || 'No matching todo found to remove.';
      },
      answer_response_artifact: async (params: Record<string, unknown>) => {
        console.log(
          '[VoiceSession] answer_response_artifact client tool called:',
          params,
        );
        if (!onAnswerArtifactRef.current) {
          return 'Answer artifact handler not available.';
        }
        try {
          const result = await onAnswerArtifactRef.current({
            artifact_id: (params.artifact_id as string) || '',
            selected_choice_ids: params.selected_choice_ids as
              | string
              | undefined,
            yes_or_no: params.yes_or_no as 'yes' | 'no' | undefined,
            form_values: params.form_values as string | undefined,
            desired_action_key: params.desired_action_key as string | undefined,
            desired_action_custom_text: params.desired_action_custom_text as
              | string
              | undefined,
            clarifying_answers: params.clarifying_answers as string | undefined,
          });
          return result;
        } catch (err) {
          console.error('[VoiceSession] answer_response_artifact error:', err);
          return `Error answering artifact: ${err instanceof Error ? err.message : 'Unknown error'}`;
        }
      },
      // NOTE: All three editor tools are registered in ElevenLabs with
      // `expects_response: false` (matching add_todo/remove_todo) because the
      // "wait for response" path in ElevenLabs requires additional schema
      // fields (compiler_mode, etc.) we don't set. To keep the agent in
      // sync, each handler pushes its outcome back via a contextual update
      // instead of returning it — the agent picks these up on its next
      // turn just like user-side edits.
      open_editor: async (params: Record<string, unknown>) => {
        console.log('[VoiceSession] open_editor client tool called:', params);
        if (!onOpenEditorRef.current) return '';
        const editorType = params.editor_type as string;
        if (editorType !== 'document' && editorType !== 'email_draft') {
          sendContextualUpdateRef.current?.(
            `[TOOL RESULT] open_editor failed: invalid editor_type "${editorType}". Must be "document" or "email_draft".`,
          );
          return '';
        }
        const cardId = onOpenEditorRef.current({
          editor_type: editorType,
          title: (params.title as string) || '',
          initial_content: params.initial_content as string | undefined,
          oauth_id:
            typeof params.oauth_id === 'number'
              ? (params.oauth_id as number)
              : typeof params.oauth_id === 'string' && params.oauth_id
                ? Number(params.oauth_id)
                : undefined,
          to: params.to as string | undefined,
          cc: params.cc as string | undefined,
          bcc: params.bcc as string | undefined,
          subject: params.subject as string | undefined,
        });
        if (cardId === 'DUPLICATE') {
          sendContextualUpdateRef.current?.(
            `[TOOL RESULT] open_editor: an editor of this type with the same title is already on screen — use patch_editor to update it rather than opening a new one.`,
          );
          return '';
        }
        sendContextualUpdateRef.current?.(
          `[TOOL RESULT] open_editor: editor opened (id: ${cardId}, type: ${editorType}). Use patch_editor with editor_id="${cardId}" to update content. Do NOT call open_editor again for this document.`,
        );
        return '';
      },
      patch_editor: async (params: Record<string, unknown>) => {
        console.log('[VoiceSession] patch_editor client tool called:', params);
        if (!onPatchEditorRef.current) return '';
        const editorId = (params.editor_id as string) || '';
        // ElevenLabs client tools don't support rich nested array-of-object
        // schemas, so `edits` arrives as a JSON string. Accept either shape.
        let edits: unknown[] = [];
        if (Array.isArray(params.edits)) {
          edits = params.edits as unknown[];
        } else if (typeof params.edits === 'string' && params.edits.trim()) {
          try {
            const parsed = JSON.parse(params.edits);
            if (Array.isArray(parsed)) edits = parsed;
          } catch (parseErr) {
            sendContextualUpdateRef.current?.(
              `[TOOL RESULT] patch_editor failed: invalid JSON in "edits" (${parseErr instanceof Error ? parseErr.message : 'parse error'}). Expected a JSON-encoded array of PatchOp objects.`,
            );
            return '';
          }
        }
        if (!editorId) {
          sendContextualUpdateRef.current?.(
            `[TOOL RESULT] patch_editor failed: missing editor_id.`,
          );
          return '';
        }
        // Optional header-field overrides. `undefined` means "don't touch",
        // empty string means "clear". Presence is detected by whether the
        // key was on the params object at all (ElevenLabs omits unset
        // string params entirely, so `hasOwnProperty` is the right check).
        const pickString = (key: string): string | undefined => {
          if (!(key in params)) return undefined;
          const v = (params as Record<string, unknown>)[key];
          return typeof v === 'string' ? v : undefined;
        };
        const titleOverride = pickString('title');
        const toOverride = pickString('to');
        const ccOverride = pickString('cc');
        const bccOverride = pickString('bcc');
        const subjectOverride = pickString('subject');
        const hasFieldOverride =
          titleOverride !== undefined ||
          toOverride !== undefined ||
          ccOverride !== undefined ||
          bccOverride !== undefined ||
          subjectOverride !== undefined;
        if (edits.length === 0 && !hasFieldOverride) {
          sendContextualUpdateRef.current?.(
            `[TOOL RESULT] patch_editor called with no edits and no field overrides — pass an "edits" array or at least one of title/to/cc/bcc/subject.`,
          );
          return '';
        }
        const result = onPatchEditorRef.current({
          editor_id: editorId,
          edits,
          title: titleOverride,
          to: toOverride,
          cc: ccOverride,
          bcc: bccOverride,
          subject: subjectOverride,
        });
        // Promote complete failures (0/N edits applied AND no field updates)
        // to a loud [ERROR] so the agent treats them as a hard failure on
        // its next turn instead of glossing past the muted [TOOL RESULT].
        const prefix =
          result.severity === 'error'
            ? '[ERROR] patch_editor'
            : '[TOOL RESULT] patch_editor';
        sendContextualUpdateRef.current?.(`${prefix}: ${result.summary}`);
        return '';
      },
      start_new_project: async (params: Record<string, unknown>) => {
        console.log(
          '[VoiceSession] start_new_project client tool called:',
          params,
        );
        if (!onShowProjectRef.current) return '';
        const cardId = onShowProjectRef.current({
          project_name: (params.project_name as string) || '',
          direction: (params.direction as string) || '',
        });
        if (cardId === 'DUPLICATE') {
          sendContextualUpdateRef.current?.(
            `[TOOL RESULT] start_new_project: a project card with this name is already on screen. Do NOT call start_new_project again for this project.`,
          );
          return '';
        }
        sendContextualUpdateRef.current?.(
          `[TOOL RESULT] start_new_project: project confirmation card "${(params.project_name as string) || ''}" is now on the user's screen (id: ${cardId}). The user can confirm or dismiss it. Do NOT call start_new_project again for this project.`,
        );
        return '';
      },
      close_editor: async (params: Record<string, unknown>) => {
        console.log('[VoiceSession] close_editor client tool called:', params);
        if (!onCloseEditorRef.current) return '';
        const editorId = (params.editor_id as string) || '';
        if (!editorId) return '';
        // Fire-and-forget — matches remove_todo. Any useful side-effects
        // (e.g. "no editor with that id") are logged client-side only.
        onCloseEditorRef.current({
          editor_id: editorId,
          reason: params.reason as string | undefined,
        });
        return '';
      },
    },
  });

  // --- OpenAI Realtime conversation ---
  const openaiConversation = useOpenAIRealtime({
    onConnect: () => {
      console.log('[VoiceSession] OpenAI connected');
      setIsConnecting(false);
      onConnectionChangeRef.current?.(true);
    },
    onDisconnect: () => {
      console.log('[VoiceSession] OpenAI disconnected');
      stopAudioLevelMonitoring();
      onConnectionChangeRef.current?.(false);
    },
    onError: (err) => {
      console.error('[VoiceSession] OpenAI error:', err);
      handleError(`Conversation error: ${err}`);
    },
    onMessage: async (message) => {
      handleVoiceMessage(message.source, message.message);
    },
    onSpeakingChange: () => {},
  });

  // --- Unified conversation state ---
  const conversation =
    voiceProvider === 'openai'
      ? {
          status:
            openaiConversation.status === 'connecting'
              ? ('connecting' as const)
              : openaiConversation.status === 'connected'
                ? ('connected' as const)
                : ('disconnected' as const),
          isSpeaking: openaiConversation.isSpeaking,
          sendContextualUpdate: openaiConversation.sendContextualUpdate,
        }
      : {
          status: elevenLabsConversation.status as VoiceSessionStatus,
          isSpeaking: elevenLabsConversation.isSpeaking,
          sendContextualUpdate: elevenLabsConversation.sendContextualUpdate,
        };

  useEffect(() => {
    if (conversation.sendContextualUpdate) {
      sendContextualUpdateRef.current = conversation.sendContextualUpdate;
    }
  }, [conversation.sendContextualUpdate]);

  // Start audio monitoring when connected
  useEffect(() => {
    if (conversation.status === 'connected') {
      const conv =
        voiceProvider === 'openai'
          ? openaiConversation
          : elevenLabsConversation;
      startAudioLevelMonitoring(voiceProvider || 'elevenlabs', conv);
    }
    return () => stopAudioLevelMonitoring();
  }, [
    conversation.status,
    voiceProvider,
    openaiConversation,
    elevenLabsConversation,
    startAudioLevelMonitoring,
    stopAudioLevelMonitoring,
  ]);

  // --- Token fetch ---
  const getConversationToken =
    useCallback(async (): Promise<WebRTCTokenResponse> => {
      const headers: Record<string, string> = {};
      const accessToken = await getAccessTokenSilently();
      headers.Authorization = `Bearer ${accessToken}`;

      const requestBody: ElevenLabsConversationTokenRequest = {
        agent_id: agentId || undefined,
        ...(threadId && { thread_id: threadId }),
        ...(voiceId && { voice_id: voiceId }),
        ...(agentFirstMessage && { agent_first_message: agentFirstMessage }),
        ...(structuredCallId && { structured_call_id: structuredCallId }),
        ...(isFirstCall && { is_first_call: true }),
        ...(dailyStandupId && { daily_standup_id: dailyStandupId }),
      };

      const response = await api.post<WebRTCTokenResponse>(
        '/webrtc/token',
        requestBody,
        { headers },
      );

      if (response.data.thread_id) {
        threadIdRef.current = response.data.thread_id;
      }
      if (response.data.tracking_id) {
        trackingIdRef.current = response.data.tracking_id;
      }

      setVoiceProvider(response.data.provider);
      voiceProviderRef.current = response.data.provider;
      return response.data;
    }, [
      agentId,
      threadId,
      voiceId,
      agentFirstMessage,
      structuredCallId,
      isFirstCall,
      dailyStandupId,
      getAccessTokenSilently,
    ]);

  // --- Start ---
  const start = useCallback(async () => {
    try {
      setError(null);
      setIsConnecting(true);
      cancelledRef.current = false;
      hasAgentSpokenRef.current = false;

      const tokenData = await getConversationToken();
      if (cancelledRef.current) return;

      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });
      if (cancelledRef.current) return;

      if (tokenData.provider === 'openai') {
        if (!tokenData.openaiConfig || !tokenData.openaiClientSecret) {
          throw new Error('OpenAI config missing from token response');
        }

        await openaiConversation.startSession({
          config: tokenData.openaiConfig,
          clientSecret: tokenData.openaiClientSecret,
        });

        // If cancelled while session was connecting, tear it down immediately
        if (cancelledRef.current) {
          await openaiConversation.endSession();
          return;
        }
      } else {
        await elevenLabsConversation.startSession({
          conversationToken: tokenData.token!,
          connectionType: 'webrtc',
          overrides: tokenData.agentConfig || {},
          customLlmExtraBody: {
            tracking_id: tokenData.tracking_id || '',
          },
          dynamicVariables: {
            tracking_id: tokenData.tracking_id || '',
          },
        });

        // If cancelled while session was connecting, tear it down immediately
        if (cancelledRef.current) {
          await elevenLabsConversation.endSession();
          return;
        }

        // Inject all confirmed memories as a contextual_update immediately
        // so the agent has them before the user speaks
        if (tokenData.memoriesContext) {
          elevenLabsConversation.sendContextualUpdate(
            tokenData.memoriesContext,
          );
          console.log(
            '[VoiceSession] Sent memories contextual_update:',
            tokenData.memoriesContext.length,
            'chars',
          );
        }
      }

      sendEventToBackend(ElevenLabsEventType.CONVERSATION_STARTED, {
        timestamp: Date.now(),
      });
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('[VoiceSession] Start error:', err);
      setIsConnecting(false);
      handleError(
        err instanceof Error ? err.message : 'Failed to start conversation',
      );
    }
  }, [
    getConversationToken,
    openaiConversation,
    elevenLabsConversation,
    sendEventToBackend,
    handleError,
  ]);

  // --- Stop ---
  const stop = useCallback(async () => {
    // Signal any in-flight start() to abort
    cancelledRef.current = true;

    const provider = voiceProviderRef.current;
    try {
      if (threadIdRef.current) {
        sendEventToBackend(ElevenLabsEventType.CONVERSATION_ENDED, {
          timestamp: Date.now(),
        });
      }

      if (provider === 'openai') {
        await openaiConversation.endSession();
      } else {
        await elevenLabsConversation.endSession();
      }
    } catch (err) {
      console.error('[VoiceSession] Stop error:', err);
    }

    stopAudioLevelMonitoring();
    setIsConnecting(false);
  }, [
    openaiConversation,
    elevenLabsConversation,
    sendEventToBackend,
    stopAudioLevelMonitoring,
  ]);

  // Keep a ref to always have the latest stop function for cleanup
  const stopRef = useRef(stop);
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  // Cleanup on unmount — end the voice session properly
  useEffect(() => {
    return () => {
      stopRef.current();
    };
  }, []);

  // Derive status
  const status: VoiceSessionStatus = isConnecting
    ? 'connecting'
    : conversation.status;

  // Stable sendContextualUpdate function
  const sendContextualUpdate = useCallback((text: string) => {
    sendContextualUpdateRef.current?.(text);
  }, []);

  return {
    start,
    stop,
    status,
    isSpeaking: conversation.isSpeaking || false,
    isThinking,
    isFirstThinking: isThinking && !hasAgentSpokenRef.current,
    audioLevel,
    inputLevel,
    outputLevel,
    outputLevelRef,
    error,
    sendContextualUpdate,
  };
}
